import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { useSocketEvent } from '@astro/socket'
import { appUrl } from '@astro/config'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import { useToast } from '../context/ToastContext'
import { useMyReview, useSubmitReview } from '../hooks/useReviews'
import ReviewForm from '../components/sections/reviews/ReviewForm'
import { StarRatingDisplay } from '../components/sections/reviews/StarRating'
import {
  unwrapBookingRecord, statusFlags, scheduleFacts, paymentFacts, joinNames,
  formatRate, astrologerDisplayName, isNoteFinalized, uploadedPhotos, birthFacts, statusMeta,
} from '../components/sections/bookingDetails/bookingDetailsData'
import { FaArrowLeft } from 'react-icons/fa'
import {
  DetailHero, SectionCard, FactRow, NoteDocument, PhotoGallery, DetailSkeleton, DetailError,
} from '../components/sections/bookingDetails/DetailBits'
import ReschedulePanel from '../components/sections/bookingDetails/ReschedulePanel'
import SuggestedRemediesSection from '../components/sections/bookingDetails/SuggestedRemediesSection'
import DownloadReportCard from '../components/sections/bookingDetails/consultationReport/DownloadReportCard'

const DASHBOARD_HREF = appUrl('website', '/dashboard?tab=consultations')

/**
 * Seeker-side booking detail — everything about one consultation, rendered
 * from GET /v1/booking-by-id/:id. The astrologer-side counterpart is
 * AppointmentDetailsPage; both read from the same endpoint. The dark
 * DetailHero doubles as the canvas the transparent site header needs; the
 * body spreads across a 12-col grid.
 */
export default function BookingDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { status: authStatus } = useAuth()
  const { showToast } = useToast()
  const [copied, setCopied] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [showReviewForm, setShowReviewForm] = useState(false)

  // Sanctum-guarded read — send seekers through login first, then back here.
  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      navigate(`/login?redirect=${encodeURIComponent(`/bookings/${id}`)}`)
    }
  }, [authStatus, id, navigate])

  const queryClient = useQueryClient()
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['booking-details', id],
    queryFn: () => api.bookings.byId(id),
    enabled: Boolean(id) && authStatus === 'authenticated',
    retry: false,
  })

  // A live session this seeker may (re)join. Rides on more than the booking
  // status because the backend can reject the ready_to_start PATCH: seeded
  // from sessionStorage (set when they first join the room, kept when they
  // Leave), raised by a live invite, cleared when the astrologer ends it.
  const [liveSession, setLiveSession] = useState(
    () => sessionStorage.getItem(`live-consultation-${id}`) === '1',
  )

  // Status changes ride the socket (astrologer started/ended the call) —
  // refetch so the Join button appears/disappears without a manual refresh.
  useSocketEvent('consultation:invite', (payload) => {
    if (String(payload?.bookingId) === String(id)) {
      sessionStorage.setItem(`live-consultation-${id}`, '1')
      setLiveSession(true)
      queryClient.invalidateQueries({ queryKey: ['booking-details', id] })
    }
  })
  useSocketEvent('consultation:end', (payload) => {
    if (String(payload?.bookingId) === String(id)) {
      sessionStorage.removeItem(`live-consultation-${id}`)
      setLiveSession(false)
      queryClient.invalidateQueries({ queryKey: ['booking-details', id] })
    }
  })

  const booking = data?.status === false ? null : unwrapBookingRecord(data)
  const bookingCode = `BKNG${id}`
  const loading = isPending || authStatus === 'loading'

  const astrologerId = booking?.astrologer?.id ?? booking?.astrologer_id ?? null
  const { myReview, isPending: myReviewPending } = useMyReview('astrologer', astrologerId)
  const submitReview = useSubmitReview('astrologer', astrologerId)

  // Deep link from My Consultations (?review=1) — auto-open once the
  // booking and the existing-review check have both loaded.
  useEffect(() => {
    if (searchParams.get('review') !== '1') return
    if (!booking || myReviewPending) return
    if (statusFlags(booking.status).isCompleted && !myReview) setShowReviewForm(true)
    const next = new URLSearchParams(searchParams)
    next.delete('review')
    setSearchParams(next, { replace: true })
  }, [searchParams, booking, myReview, myReviewPending, setSearchParams])

  const handleSubmitReview = async (payload) => {
    await submitReview.mutateAsync(payload)
    setShowReviewForm(false)
    showToast('Thank you — your review has been posted.')
  }

  const schedule = booking ? scheduleFacts(booking) : null
  const heroFacts = booking
    ? [
        { label: 'Astrologer', value: astrologerDisplayName(booking) },
        {
          label: 'Consultation',
          value: `${booking.category === 'gemstone' ? 'Gemstone · ' : ''}${(booking.consultation_type || 'Consultation').replace(/^\w/, (c) => c.toUpperCase())}`,
        },
        { label: 'Date', value: schedule.dateLabel },
        { label: 'Slot', value: schedule.slotLabel },
        { label: 'Duration', value: schedule.durationLabel },
        { label: 'Price', value: formatRate(booking.rate) },
        { label: 'Status', value: <span className={statusMeta(booking.status).heroCls}>{statusMeta(booking.status).label}</span> },
      ]
    : []

  const copyBookingId = () => {
    navigator.clipboard?.writeText(bookingCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  const renderBody = () => {
    if (loading) return <DetailSkeleton />
    if (isError || !booking) {
      return <DetailError id={id} onRetry={refetch} backHref={DASHBOARD_HREF} backLabel="My Consultations" />
    }

    const flags = statusFlags(booking.status)
    const payment = paymentFacts(booking)
    const astro = booking.astrologer || {}
    const noteFinalized = isNoteFinalized(booking)
    const photos = uploadedPhotos(booking)
    const birth = birthFacts(booking)
    // Rejoin path: the session is live (per socket/room, even if the status
    // PATCH was rejected upstream) and the booking hasn't been closed out.
    const canJoinLive = flags.canJoin || (liveSession && !flags.isCompleted && !flags.isCancelled)

    // The astrologer has opened the room (status ready_to_start/in_progress)
    // — the room is resolved from the booking id inside the video page, and
    // joining PATCHes the booking to in_progress from there.
    const joinConsultation = () => {
      navigate(`/consultations/${booking.id}/video`)
    }

    return (
      <div className="container px-6 mx-auto pt-8 pb-24">
        {/* Cancelled banner */}
        {flags.isCancelled && (
          <div className="rounded-2xl border border-red-200 bg-red-50/60 px-6 py-4 text-sm text-red-600 mb-6">
            This consultation has been cancelled. If you were charged, the refund will reflect in 5–7 working days.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* ── Main column ── */}
          <div className="lg:col-span-8 space-y-6">
            {canJoinLive && (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={joinConsultation}
                  className="hero-btn-primary inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em]"
                >
                  📹 {liveSession && !flags.canJoin ? 'Rejoin Consultation' : 'Join Consultation'}
                </button>
              </div>
            )}

            {/* Who's on each side of the session */}
            <SectionCard title="Astrologer Details" icon="🔮">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1.5">
                <FactRow label="Name" value={astrologerDisplayName(booking)} />
                <FactRow label="Experience" value={astro.experience != null ? `${astro.experience} Years` : '—'} />
                <FactRow label="Skills" value={joinNames(astro.skills) || '—'} />
                <FactRow label="Languages" value={joinNames(astro.languages) || '—'} />
              </div>
            </SectionCard>

            <SectionCard title="Your Information" icon="🙏">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1.5">
                <FactRow label="Name" value={booking.name || '—'} />
                <FactRow label="Email" value={booking.email || '—'} />
                <FactRow label="Phone" value={booking.phone || '—'} />
                <FactRow label="Birth Date" value={birth.date || '—'} />
                <FactRow label="Birth Time" value={birth.time || '—'} />
                <FactRow label="Birth Place" value={birth.place || '—'} />
              </div>
              {booking.notes && (
                <div className="mt-5 pt-4 border-t border-[#C8922A]/15">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-1.5">Booking Notes</p>
                  <p className="text-sm text-[#403c3d] whitespace-pre-line">{booking.notes}</p>
                </div>
              )}
            </SectionCard>

            {/* Face/palm photos submitted with the booking — only shown when at least one is present */}
            {photos.length > 0 && (
              <SectionCard title="Uploaded Photos" icon="🖼️">
                <PhotoGallery photos={photos} />
              </SectionCard>
            )}

            {/* Remedies the astrologer pinned to this consultation — polls
                while the session is live so in-call suggestions appear
                without a refresh; hidden when there are none. */}
            <SuggestedRemediesSection booking={booking} live={canJoinLive} />

            {/* Astrologer note — shown only once finalized on the astrologer's side */}
            <SectionCard title="Astrologer Note" icon="📜">
              {noteFinalized ? (
                <NoteDocument booking={booking} />
              ) : (
                <p className="text-sm text-[#8B7355]">No note has been shared by the astrologer yet.</p>
              )}
            </SectionCard>

            {/* Review CTA — only once the consultation is done; hidden entirely
                for astrologer-less flows (astrologerId missing) since a review
                needs a reviewable_id to submit against. */}
            {flags.isCompleted && astrologerId && (
              <SectionCard title="Rate Your Consultation" icon="⭐">
                {myReviewPending ? (
                  <div className="h-5 w-40 rounded bg-stone-100 animate-pulse" />
                ) : myReview ? (
                  <div className="flex items-center gap-3">
                    <StarRatingDisplay value={myReview.rating} size="md" />
                    <span className="text-sm text-[#8B7355]">You&rsquo;ve already reviewed this consultation.</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-sm text-[#8B7355]">How was your session with {astrologerDisplayName(booking)}?</p>
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(true)}
                      className="hero-btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.2em]"
                    >
                      ✍️ Write a Review
                    </button>
                  </div>
                )}
              </SectionCard>
            )}
          </div>

          {/* ── Sidebar ── */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-40">
            {/* The whole consultation as a PDF — once the session is done */}
            {flags.isCompleted && <DownloadReportCard booking={booking} />}

            <SectionCard>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B7355]">Total Amount</p>
              <p className="font-cinzel text-4xl text-[#1C140A] mt-3">{formatRate(booking.rate)}</p>
              <p className="text-xs text-[#8B7355] mt-2">Consultation payment has been received and recorded.</p>
            </SectionCard>

            <SectionCard title="Payment Details" icon="💳">
              <div className="space-y-1.5">
                <FactRow label="Method" value={payment.methodLabel} />
                <FactRow
                  label="Transaction ID"
                  value={payment.transactionId ? <span className="font-mono text-xs">{payment.transactionId}</span> : '—'}
                />
                <FactRow label="Status" value={<span className="text-green-600">Paid</span>} />
              </div>
            </SectionCard>

            {/* Reschedule — locked once the session is ready/live/done */}
            {flags.canReschedule && (
              <ReschedulePanel
                booking={booking}
                onDone={() => showToast('Booking rescheduled.')}
                onError={(message) => showToast(message)}
              />
            )}
          </aside>
        </div>
      </div>
    )
  }

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="grow">
        <DetailHero
          backHref={DASHBOARD_HREF}
          backLabel="My Consultations"
          title="Consultation Details"
          code={bookingCode}
          facts={heroFacts}
          action={
            /* Straight back to the dashboard's consultations tab — the
               landing spot after a session ends. */
            <a
              href={DASHBOARD_HREF}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] border border-secondary/40 text-secondary hover:bg-secondary/10 transition-colors"
            >
              <FaArrowLeft className="text-xs" />
              My Consultations
            </a>
          }
          onCopy={booking ? copyBookingId : undefined}
          copied={copied}
        />
        {renderBody()}
      </main>
      {showReviewForm && booking && astrologerId && (
        <ReviewForm
          astrologerName={astrologerDisplayName(booking)}
          onSubmit={handleSubmitReview}
          onClose={() => setShowReviewForm(false)}
        />
      )}
      <FloatingFabs />
    </PageLayout>
  )
}
